import { PropsWithChildren } from 'react';
import { Protected } from '@britishcouncil/react-common';

import { FullScreenLoader } from 'components';
import {
  useGetExaminerQuery,
  useGetPermissionsQuery,
  useHasRequiredTrainingForOdpQuery,
} from 'store';
import { NoAccess } from './NoAccess';
import { useExaminerId } from './examiner/useExaminerId';
import { Permission } from 'api';

interface ProtectedPageProps extends PropsWithChildren {
  requiresAll?: Array<string | number>;
  requiresAny?: Array<string | number>;
}

export const ProtectedPage = ({ requiresAll, requiresAny, children }: ProtectedPageProps) => {
  const { isError, isSuccess } = useGetPermissionsQuery();
  const permissionsLoaded = isError || isSuccess;

  return permissionsLoaded ? (
    <Protected requiresAll={requiresAll} requiresAny={requiresAny} fallbackComponent={<NoAccess />}>
      {children}
    </Protected>
  ) : (
    <FullScreenLoader />
  );
};

type ProtectedExaminerPageLogicProps = PropsWithChildren<{
  requiresAnyRole?: string[];
  requiresTrainingForOdp?: boolean;
  permissions: Permission[];
}>;

const ProtectedExaminerPageLogic = ({
  requiresAnyRole,
  requiresTrainingForOdp,
  permissions,
  children,
}: ProtectedExaminerPageLogicProps) => {
  const examinerId = useExaminerId();
  const {
    data: examiner,
    isError: examinerError,
    isSuccess: examinerSuccess,
  } = useGetExaminerQuery(examinerId);

  const examinerLoaded = examinerError || examinerSuccess;

  const hasViewOdpPermission = permissions.includes(Permission.ViewOdp);

  const {
    data: hasRequiredTrainingForOdp,
    isError: hasRequiredTrainingForOdpError,
    isSuccess: hasRequiredTrainingForOdpSuccess,
  } = useHasRequiredTrainingForOdpQuery(examinerId, {
    skip: !requiresTrainingForOdp || !hasViewOdpPermission,
  });

  const hasRequiredTrainingForOdpLoaded =
    !requiresTrainingForOdp || hasRequiredTrainingForOdpError || hasRequiredTrainingForOdpSuccess;

  if (!examinerLoaded || !hasRequiredTrainingForOdpLoaded) {
    return <FullScreenLoader />;
  }

  if (
    requiresAnyRole &&
    !examiner?.assignedRolesIds?.some((roleId) => requiresAnyRole.includes(roleId))
  ) {
    return (
      <NoAccess title="" text="Examiner doesn't have the required role to access this page." />
    );
  }

  if (requiresTrainingForOdp && !hasRequiredTrainingForOdp) {
    return (
      <NoAccess
        title=""
        text="Examiner must complete the required ODP training to access this page."
      />
    );
  }

  return <>{children}</>;
};

export const ProtectedExaminerPage = ({
  requiresAll,
  requiresAny,
  requiresAnyRole,
  requiresTrainingForOdp,
  children,
}: ProtectedPageProps & { requiresAnyRole?: string[]; requiresTrainingForOdp?: boolean }) => {
  const {
    isError: permissionsError,
    isSuccess: permissionsSuccess,
    data: permissions,
  } = useGetPermissionsQuery();
  const permissionsLoaded = permissionsError || permissionsSuccess;

  if (!permissionsLoaded) {
    return <FullScreenLoader />;
  }

  return (
    <Protected requiresAll={requiresAll} requiresAny={requiresAny} fallbackComponent={<NoAccess />}>
      <ProtectedExaminerPageLogic
        requiresAnyRole={requiresAnyRole}
        requiresTrainingForOdp={requiresTrainingForOdp}
        permissions={permissions!}
      >
        {children}
      </ProtectedExaminerPageLogic>
    </Protected>
  );
};
